import type { RecoProductCardFragment } from '@packages/gql/generated/shopping/graphql';
import type { ProductCardV2EnergyEfficiencyFlagsLegacyProps } from '@packages/modules/src/ProductCardV2/ProductCardV2.EnergyEfficiencyFlagsLegacy';
import type { ProductCardV2TireEfficiencyFlagsProps } from '@packages/modules/src/ProductCardV2/ProductCardV2.TireEfficiencyFlags.client';
import type { TireEfficiencyFlagSpecificProps } from '@packages/shared/src/ui/TireEfficiencyFlag/TireEfficiencyFlag';
import type { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl';

/**
 * This maps the type from our product data to the correct format for the component `EfficiencyFlag`
 */
const tireEfficiencyTypeMap: Record<string, TireEfficiencyFlagSpecificProps['type']> = {
  WETGRIP: 'wetGrip',
  FUELEFFICIENCY: 'fuelEfficiency',
  EXTERNALROLLINGNOISE: 'externalRollingNoise',
};

const messages = defineMessages({
  WETGRIP: {
    defaultMessage: 'Nasshaftung',
    id: 'recoProductCard.type.wetgrip',
  },
  FUELEFFICIENCY: {
    defaultMessage: 'Kraftstoffeffizienz',
    id: 'recoProductCard.type.fuelEfficiency',
  },
  EXTERNALROLLINGNOISE: {
    defaultMessage: 'Rollgeräusch',
    id: 'recoProductCard.type.externalRollingNoise',
  },
});

export const getTireEfficiencyFlags = (
  tireFlags: NonNullable<RecoProductCardFragment['product']>['tireEfficiency'],
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => {
  const flags: ProductCardV2TireEfficiencyFlagsProps['flags'] = [];

  if (tireFlags && tireFlags.flags.length > 0) {
    tireFlags.flags.forEach((flag) => {
      const overlay: ProductCardV2TireEfficiencyFlagsProps['flags'][0]['overlay'] = {
        title: formatMessage(messages[flag.type]),
        imgUrl: tireFlags.link,
      };

      flags.push({
        type: tireEfficiencyTypeMap[flag.type],
        text: flag.level,
        size: 'normal',
        overlay,
      });
    });
  }

  return flags;
};

export const getLegacyTireEfficiencyFlags = (
  tireFlags: NonNullable<RecoProductCardFragment['product']>['tireEfficiency'],
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): ProductCardV2EnergyEfficiencyFlagsLegacyProps['flags'] =>
  tireFlags
    ? tireFlags.flags.map((flag) => ({
        type: tireEfficiencyTypeMap[flag.type],
        text: flag.level,
        size: 'small',
        overlay: {
          title: formatMessage(messages[flag.type]),
          imgUrl: tireFlags.link,
        },
      }))
    : [];
